/* eslint-disable max-len */

import { BASE_ROUTES } from 'core/common/constants';
import { useConfig } from 'core/common/hooks';
import { TechLayout } from 'core/common/ui/layouts';
import { Typography } from 'core/common/ui/ui-kit';
import { Locales } from 'core/user/entities';
import { Location, LocationOption } from 'core/user/ui';
import { usePrivacyPolicy } from './usePrivacyPolicy';
import {
  BoldStyled,
  ContainerStyled,
  LetterListStyled,
  LinkStyled,
  ListItemStyled,
  NumberListStyled,
  UnderlineStyled,
  UnorderedListStyled,
  WrapperStyled,
} from './styled';

const createSectionParams = (id: string) => ({
  id,
  href: `#${id}`,
});

const sections = {
  1: createSectionParams('section1'),
  2: createSectionParams('section2'),
  3: createSectionParams('section3'),
  4: createSectionParams('section4'),
  5: createSectionParams('section5'),
  6: createSectionParams('section6'),
  7: createSectionParams('section7'),
  8: createSectionParams('section8'),
  9: createSectionParams('section9'),
  10: createSectionParams('section10'),
  11: createSectionParams('section11'),
  12: createSectionParams('section12'),
  13: createSectionParams('section13'),
  14: createSectionParams('section14'),
};

export const PrivacyPolicy = () => {
  const config = useConfig();
  usePrivacyPolicy();

  return (
    <TechLayout>
      <Typography textAlign="center" gutterBottom variant="h1">
        IMPORTANT PRIVACY INFORMATION
      </Typography>
      <Typography variant="bodyMediumRegular" gutterBottom>
        When you access our service we automatically collect from your device language settings, IP
        address, time zone, type and model of a device, device settings, operating system, Internet
        service provider, mobile carrier, hardware ID, Facebook ID, and other unique identifiers
        (such as IDFA and AAID). We need this data to provide our services, analyze how our
        customers use the service and to measure ads.
      </Typography>
      <Typography variant="bodyMediumRegular" gutterBottom>
        For improving the product and serving ads, we use third party solutions. As a result, we may
        process data using solutions developed by{' '}
        <BoldStyled>
          Amazon, Snapchat, Facebook, Google, Hotjar, Amplitude, Firebase, Pinterest, Snapchat,
          TikTok
        </BoldStyled>
        . Therefore, some of the data is stored and processed on the servers of such third parties.
        This enables us to (1) analyze different interactions (what products our users have viewed);
        (2) serve ads (and show them only to a particular group of users, for example, only to
        subscribers).
      </Typography>
      <Typography variant="bodyMediumRegular" gutterBottom>
        Please read our Privacy Policy below to know more about what we do with data (
        <LinkStyled href={sections[3].href}>Section 3</LinkStyled>) , what data privacy rights are
        available to you (<LinkStyled href={sections[6].href}>Section 6</LinkStyled>) and who will
        be the data controller (<LinkStyled href={sections[1].href}>Section 1</LinkStyled>
        ). If any questions will remain unanswered, please contact us at{' '}
        <LinkStyled href={config.supportCenter} target="_blank">
          Support Center
        </LinkStyled>
        .
      </Typography>
      <Typography gutterBottom align="center" variant="h3">
        PRIVACY POLICY
      </Typography>
      <Typography variant="bodyMediumRegular" gutterBottom>
        This Privacy Policy explains what personal data is collected when you use the Nebula mobile
        application (the “App”), Nebula website located at{' '}
        <BoldStyled>{config.mainAppUrl}</BoldStyled> (the “Website”), the services and products
        provided through it (together Website with the App, the <BoldStyled>“Service”</BoldStyled>),
        how such personal data will be processed.
      </Typography>
      <Typography variant="bodyMediumRegular" gutterBottom>
        BY USING THE SERVICE, YOU PROMISE US THAT (I) YOU HAVE READ, UNDERSTAND AND AGREE TO THIS
        PRIVACY POLICY, AND (II) YOU ARE OVER 16 YEARS OF AGE (OR HAVE HAD YOUR PARENT OR GUARDIAN
        READ AND AGREE TO THIS PRIVACY POLICY FOR YOU). If you do not agree, or are unable to make
        this promise, you must not use the Service. In such case, you must (a) contact us and
        request deletion of your data; and (b) cancel any subscriptions using the functionality
        provided by Apple (if you are using iOS) or Google (if you are using Android), any other app
        stores that may be available from time to time, or instructions on the Website; (c) delete
        the App from your devices; (d) leave the Website and not access or use it.
      </Typography>
      <Typography variant="bodyMediumRegular" gutterBottom>
        <BoldStyled>“GDPR”</BoldStyled> means the General Data Protection Regulation (EU) 2016/679
        of the European Parliament and of the Council of 27 April 2016 on the protection of natural
        persons with regard to the processing of personal data and on the free movement of such
        data.
      </Typography>
      <Typography variant="bodyMediumRegular" gutterBottom>
        <BoldStyled>“EEA”</BoldStyled> includes all current member states to the European Union and
        the European Free Trade Association. For the purpose of this policy EEA shall include the
        United Kingdom of Great Britain and Northern Ireland.
      </Typography>
      <Typography variant="bodyMediumRegular" gutterBottom>
        <BoldStyled>“Process”</BoldStyled>, in respect of personal data, includes to collect, store,
        and disclose to others.
      </Typography>
      <Typography variant="h4" gutterBottom>
        TABLE OF CONTENTS
      </Typography>
      <NumberListStyled>
        <ListItemStyled>
          <LinkStyled href={sections[1].href}>PERSONAL DATA CONTROLLER</LinkStyled>
        </ListItemStyled>
        <ListItemStyled>
          <LinkStyled href={sections[2].href}>CATEGORIES OF PERSONAL DATA WE COLLECT</LinkStyled>
        </ListItemStyled>
        <ListItemStyled>
          <LinkStyled href={sections[3].href}>
            FOR WHAT PURPOSES WE PROCESS PERSONAL DATA
          </LinkStyled>
        </ListItemStyled>
        <ListItemStyled>
          <LinkStyled href={sections[4].href}>
            UNDER WHAT LEGAL BASES WE PROCESS YOUR PERSONAL DATA (Applies only to EEA-based users)
          </LinkStyled>
        </ListItemStyled>
        <ListItemStyled>
          <LinkStyled href={sections[5].href}>WITH WHOM WE SHARE YOUR PERSONAL DATA</LinkStyled>
        </ListItemStyled>
        <ListItemStyled>
          <LinkStyled href={sections[6].href}>HOW YOU CAN EXERCISE YOUR PRIVACY RIGHTS</LinkStyled>
        </ListItemStyled>
        <ListItemStyled>
          <LinkStyled href={sections[7].href}>AGE LIMITATION</LinkStyled>
        </ListItemStyled>
        <ListItemStyled>
          <LinkStyled href={sections[8].href}>INTERNATIONAL DATA TRANSFERS</LinkStyled>
        </ListItemStyled>
        <ListItemStyled>
          <LinkStyled href={sections[9].href}>CHANGES TO THIS PRIVACY POLICY</LinkStyled>
        </ListItemStyled>
        <ListItemStyled>
          <LinkStyled href={sections[10].href}>FOR VIRGINIA RESIDENTS</LinkStyled>
        </ListItemStyled>
        <ListItemStyled>
          <LinkStyled href={sections[11].href}>CALIFORNIA PRIVACY RIGHTS</LinkStyled>
        </ListItemStyled>
        <ListItemStyled>
          <LinkStyled href={sections[12].href}>DATA RETENTION</LinkStyled>
        </ListItemStyled>
        <ListItemStyled>
          <LinkStyled href={sections[13].href}>HOW “DO NOT TRACK” REQUESTS ARE HANDLED</LinkStyled>
        </ListItemStyled>
        <ListItemStyled>
          <LinkStyled href={sections[14].href}>CONTACT US</LinkStyled>
        </ListItemStyled>
      </NumberListStyled>
      <NumberListStyled>
        <ListItemStyled id={sections[1].id}>
          <BoldStyled>PERSONAL DATA CONTROLLER</BoldStyled>
          <Typography variant="bodyMediumRegular" gutterBottom>
            <Location>
              <LocationOption option={Locales.US}>
                YOLO BROTHERS INC, a legal entity incorporated under the laws of Delaware
              </LocationOption>
              <LocationOption option={Locales.AE}>FT SICH FZCO</LocationOption>
              <LocationOption option={Locales.OTHER}>
                {config.companyName}, a company registered under the laws of the Republic of Cyprus
              </LocationOption>
            </Location>
            , having its registered office at{' '}
            <Location>
              <LocationOption option={Locales.US}>{config.addressUS}</LocationOption>
              <LocationOption option={Locales.AE}>
                IFZA Business Park, DDP, Building A2, 21379-001, Dubai, United Arab Emirates
              </LocationOption>
              <LocationOption option={Locales.OTHER}>{config.address}</LocationOption>
            </Location>{' '}
            will be the controller of your personal data.
          </Typography>
        </ListItemStyled>
        <ListItemStyled id={sections[2].id}>
          <BoldStyled>CATEGORIES OF PERSONAL DATA WE COLLECT</BoldStyled>
          <Typography variant="bodyMediumRegular" gutterBottom>
            We collect data you give us voluntarily (for example, name or email address). We also
            collect data automatically (for example, your IP address).
          </Typography>
          <ContainerStyled>
            <NumberListStyled>
              <ListItemStyled>
                <BoldStyled>Data you give us</BoldStyled>
                <Typography variant="bodyMediumRegular" gutterBottom>
                  We may ask you to provide information about yourself when you register for and/or
                  use the Service. This information includes: email address, name, gender, date of
                  birth, place of birth, and time of birth. We may also ask you to provide the date
                  of birth, place of birth, and time of birth of your partner.
                </Typography>
              </ListItemStyled>
              <ListItemStyled>
                <BoldStyled>Data we collect automatically:</BoldStyled>
                <ContainerStyled>
                  <NumberListStyled>
                    <ListItemStyled>
                      <BoldStyled>Data about how you found us</BoldStyled>
                      <Typography variant="bodyMediumRegular" gutterBottom>
                        We collect data about your referring app or URL (that is, the app or place
                        on the Web where you were when you tapped on our ad).
                      </Typography>
                    </ListItemStyled>
                    <ListItemStyled>
                      <BoldStyled>Device and Location data</BoldStyled>
                      <Typography variant="bodyMediumRegular" gutterBottom>
                        We collect data from your mobile device. Examples of such data include:
                        language settings, IP address, time zone, type and model of a device, device
                        settings, operating system, Internet service provider, mobile carrier,
                        hardware ID, and Facebook ID.
                      </Typography>
                    </ListItemStyled>
                    <ListItemStyled>
                      <BoldStyled>Usage data</BoldStyled>
                      <Typography variant="bodyMediumRegular" gutterBottom>
                        We record how you interact with our Service. For example, we log your
                        taps/clicks on certain areas of the interface, the features, and content you
                        interact with, how often you use the Service, how long you are in the
                        Service, your purchase orders.
                      </Typography>
                    </ListItemStyled>
                    <ListItemStyled>
                      <BoldStyled>Advertising IDs</BoldStyled>
                      <Typography variant="bodyMediumRegular" gutterBottom>
                        We collect your Apple Identifier for Advertising (“IDFA”) or Google
                        Advertising ID (“AAID”) (depending on the operating system of your device).
                        You can typically reset these numbers through the settings of your device’s
                        operating system (but we do not control this).
                      </Typography>
                    </ListItemStyled>
                    <ListItemStyled>
                      <BoldStyled>Cookies</BoldStyled>
                      <Typography variant="bodyMediumRegular" gutterBottom>
                        A cookie is a small text file that is stored on a user’s computer for
                        record-keeping purposes. Cookies can be either session cookies or persistent
                        cookies. A session cookie expires when you close your browser and is used to
                        make it easier for you to navigate our Service. A persistent cookie remains
                        on your hard drive for an extended period of time. We also use tracking
                        pixels that set cookies to assist with delivering online advertising.Cookies
                        are used, in particular, to automatically recognize you the next time you
                        visit our Website. As a result, the information, which you have earlier
                        entered in certain fields on the Website may automatically appear the next
                        time when you use our Service. Cookie data will be stored on your device and
                        most of the times only for a limited time period.
                      </Typography>
                    </ListItemStyled>
                    <ListItemStyled>
                      <BoldStyled>Meta Pixel</BoldStyled>
                      <Typography variant="bodyMediumRegular" gutterBottom>
                        We use Meta Pixel to collect data about your actions on our Service. This
                        data may include which pages you visit, the time you spend on each page, and
                        the actions you take. The data collected is used to measure the
                        effectiveness of our advertising campaigns and to personalize the content
                        and ads presented to you. The information collected through Meta Pixel is
                        transmitted to Meta. Your browser establishes a direct connection with the
                        Meta server as soon as you have agreed to the use of tracking technologies
                        requiring your consent.The processing of this data by Meta takes place
                        within the framework of Meta’s{' '}
                        <LinkStyled href="https://www.facebook.com/privacy/policy/?entry_point=data_policy_redirect&entry=0">
                          data policy
                        </LinkStyled>
                        . To opt-out from Meta’s interest-based ads follow these instructions from
                        Meta:{' '}
                        <LinkStyled href="https://www.facebook.com/help/568137493302217">
                          Adjust how ads on Facebook are shown to you based on your Activity
                          information from ad partners | Facebook Help Center
                        </LinkStyled>
                      </Typography>
                    </ListItemStyled>
                  </NumberListStyled>
                </ContainerStyled>
              </ListItemStyled>
            </NumberListStyled>
          </ContainerStyled>
        </ListItemStyled>
        <ListItemStyled id={sections[3].id}>
          <BoldStyled>FOR WHAT PURPOSES WE PROCESS YOUR PERSONAL DATA</BoldStyled>
          <ContainerStyled>
            <Typography variant="bodyMediumRegular" gutterBottom>
              We process your personal data:
            </Typography>
            <NumberListStyled>
              <ListItemStyled>
                <BoldStyled>To provide our Service</BoldStyled>
                <Typography variant="bodyMediumRegular" gutterBottom>
                  This includes enabling you to use the Service in a seamless manner and preventing
                  or addressing Service errors or technical issues.
                </Typography>
                <Typography variant="bodyMediumRegular" gutterBottom>
                  To host personal data and enable our Service to operate and be distributed we use{' '}
                  <BoldStyled>Amazon Web Services</BoldStyled>, which is a hosting and backend
                  service provided by Amazon.
                </Typography>
              </ListItemStyled>
              <ListItemStyled>
                <BoldStyled>To customize your experience</BoldStyled>
                <Typography variant="bodyMediumRegular" gutterBottom>
                  We process your personal data, such as age, gender, date of birth, to adjust the
                  content of the Service and provide content tailored to your personal preferences.
                </Typography>
              </ListItemStyled>
              <ListItemStyled>
                <BoldStyled>To provide you with customer support</BoldStyled>
                <Typography variant="bodyMediumRegular" gutterBottom>
                  We process your personal data to respond to your requests for technical support,
                  Service information or to any other communication you initiate. For this purpose,
                  we may send you emails about, for example, the performance of our Service, payment
                  transactions, notices regarding our Terms and Conditions of Use, this Privacy
                  Policy or other documents that we make available from time to time.
                </Typography>
              </ListItemStyled>
              <ListItemStyled>
                <BoldStyled>To communicate with you regarding your use of our Service</BoldStyled>
                <Typography variant="bodyMediumRegular" gutterBottom>
                  We communicate with you, for example, by push notifications. These may include
                  horoscope reminders, motivational messages or other information about the Service.
                  As a result, you may, for example, receive a push notification every day at a
                  particular time reminding you to check your horoscope. To opt out of receiving
                  push notifications, you need to change the settings on your device. We can also
                  communicate with you via email when you make a purchase on the Website. These may
                  include emails with information about the Service.
                </Typography>
                <Typography variant="bodyMediumRegular" gutterBottom>
                  The services that we use for these purposes may collect data concerning the date
                  and time when the message was viewed by our Services’ users, as well as when they
                  interacted with it, such as by tapping/clicking on links included in the message.
                </Typography>
              </ListItemStyled>
              <ListItemStyled>
                <BoldStyled>To research and analyze your use of the Service</BoldStyled>
                <Typography variant="bodyMediumRegular" gutterBottom>
                  This helps us to better understand our business, analyze our operations, maintain,
                  improve, innovate, plan, design, and develop the Service and our new products. We
                  also use such data for statistical analysis purposes, to test and improve our
                  offers. This enables us to better understand what categories of users use our
                  Services. As a consequence, we often decide how to improve the Service based on
                  the results obtained from this processing.
                </Typography>
                <Typography variant="bodyMediumRegular" gutterBottom>
                  We use Facebook Analytics, which is a service provided by Facebook that allows us
                  to use different analytical tools. On Facebook Analytics we get, in particular,
                  aggregated demographics and insights on how many people launch our App, how often
                  users make purchases, and other interactions. Learn more about Facebook’s approach
                  to data from its Privacy Policy.
                </Typography>
                <Typography variant="bodyMediumRegular" gutterBottom>
                  Amplitude is an analytics service provided by Amplitude Inc. We use this tool to
                  understand how customers use our Service. Amplitude collects various technical
                  information, in particular, time zone, type of device (phone, tablet or laptop),
                  unique identifiers (including advertising identifiers). Amplitude also allows us
                  to track various interactions that occur in our App. As a result, Amplitude helps
                  us to decide what features we should focus on. Amplitude provides more information
                  on how they process data in its Privacy Policy.
                </Typography>
                <Typography variant="bodyMediumRegular" gutterBottom>
                  We also use Firebase Analytics, which is an analytics service provided by Google.
                  In order to understand Google`s use of data, consult Google`s partner policy.
                  Firebase Privacy information. Google`s Privacy Policy.
                </Typography>
                <Typography variant="bodyMediumRegular" gutterBottom>
                  We also use Hotjar, a behavior analytics tool that analyses Website use, providing
                  feedback through tools such as heatmaps, session recordings, and surveys. Hotjar’s
                  Privacy Policy.
                </Typography>
              </ListItemStyled>
              <ListItemStyled>
                <BoldStyled>To send you marketing communications</BoldStyled>
                <Typography variant="bodyMediumRegular" gutterBottom>
                  We may show you advertisements on our Service, send you push notifications or
                  emails for marketing purposes. To opt out of receiving push notifications from us,
                  you need to change the settings on your device. To opt out of receiving marketing
                  emails, you need to unsubscribe following instructions in the footer of the
                  marketing emails.
                </Typography>
              </ListItemStyled>
              <ListItemStyled>
                <BoldStyled>To personalize our ads</BoldStyled>
                <Typography variant="bodyMediumRegular" gutterBottom>
                  We and our partners use your personal data to tailor ads and possibly even show
                  them to you at the relevant time. For example, if you have installed our App, you
                  might see ads of our products in your Facebook’s feed.
                </Typography>
                <Typography variant="bodyMediumRegular" gutterBottom>
                  <BoldStyled>How to opt out or influence personalized advertising</BoldStyled>
                </Typography>
                <Typography variant="bodyMediumRegular" gutterBottom>
                  <BoldStyled>iOS</BoldStyled>: On your iPhone or iPad, go to “Settings,” then
                  “Privacy” and tap “Advertising” to select “Limit Ad Track”. In addition, you can
                  reset your advertising identifier (this also may help you to of personalized ads)
                  in the same section.
                </Typography>
                <Typography variant="bodyMediumRegular" gutterBottom>
                  <BoldStyled>Android</BoldStyled>: To opt-out of ads on an Android device, go to
                  Settings &gt; Privacy &gt; Ads and enable Opt out of Ads personalization. In
                  addition, you can reset your advertising identifier in the same section (this also
                  may help you to see less of personalized ads). To learn even more about how to
                  affect advertising choices on various devices, please look at the information
                  available{' '}
                  <LinkStyled href="https://www.networkadvertising.org/mobile-choice">
                    here
                  </LinkStyled>
                  .
                </Typography>
                <Typography variant="bodyMediumRegular" gutterBottom>
                  In addition, you may get useful information and opt out of some interest-based
                  advertising, by visiting the following links:
                </Typography>
                <UnorderedListStyled>
                  <ListItemStyled>
                    Network Advertising Initiative –{' '}
                    <LinkStyled href="http://optout.networkadvertising.org/">
                      http://optout.networkadvertising.org/
                    </LinkStyled>
                  </ListItemStyled>
                  <ListItemStyled>
                    Digital Advertising Alliance –{' '}
                    <LinkStyled href="http://optout.aboutads.info/">
                      http://optout.aboutads.info/
                    </LinkStyled>
                  </ListItemStyled>
                  <ListItemStyled>
                    Digital Advertising Alliance (Canada) –{' '}
                    <LinkStyled href="http://youradchoices.ca/choices">
                      http://youradchoices.ca/choices
                    </LinkStyled>
                  </ListItemStyled>
                  <ListItemStyled>
                    Digital Advertising Alliance (EU) –{' '}
                    <LinkStyled href="http://www.youronlinechoices.com/">
                      http://www.youronlinechoices.com/
                    </LinkStyled>
                  </ListItemStyled>
                  <ListItemStyled>
                    DAA AppChoices page –{' '}
                    <LinkStyled href="http://www.aboutads.info/appchoices">
                      http://www.aboutads.info/appchoices
                    </LinkStyled>
                  </ListItemStyled>
                </UnorderedListStyled>
                <Typography variant="bodyMediumRegular" gutterBottom>
                  <BoldStyled>Browsers</BoldStyled>: It is also may be possible to stop your browser
                  from accepting cookies altogether by changing your browser’s cookie settings. You
                  can usually find these settings in the “options” or “preferences” menu of your
                  browser. The following links may be helpful, or you can use the “Help” option in
                  your browser
                </Typography>
                <UnorderedListStyled>
                  <ListItemStyled>
                    <LinkStyled href="http://windows.microsoft.com/en-GB/internet-explorer/delete-manage-cookies#ie=ie-10">
                      Cookie settings in Internet Explorer
                    </LinkStyled>
                  </ListItemStyled>
                  <ListItemStyled>
                    <LinkStyled href="http://support.mozilla.com/en-US/kb/Cookies">
                      Cookie settings in Firefox
                    </LinkStyled>
                  </ListItemStyled>
                  <ListItemStyled>
                    <LinkStyled href="https://support.google.com/chrome/answer/95647?hl=en&ref_topic=14666">
                      Cookie settings in Chrome
                    </LinkStyled>
                  </ListItemStyled>
                  <ListItemStyled>
                    <LinkStyled href="https://support.google.com/chrome/answer/95647?hl=en&ref_topic=14666">
                      Cookie settings in Safari web
                    </LinkStyled>{' '}
                    and <LinkStyled href="http://support.apple.com/kb/HT1677">iOS</LinkStyled>
                  </ListItemStyled>
                </UnorderedListStyled>
                <Typography variant="bodyMediumRegular" gutterBottom>
                  Google allows its users to{' '}
                  <LinkStyled href="https://adssettings.google.com/authenticated?hl=ru">
                    opt out of Google’s personalized ads
                  </LinkStyled>{' '}
                  and to{' '}
                  <LinkStyled href="https://tools.google.com/dlpage/gaoptout/">
                    prevent their data from being used by Google Analytics
                  </LinkStyled>
                  .
                </Typography>
                <Typography variant="bodyMediumRegular" gutterBottom>
                  We value your right to influence the ads that you see, thus we are letting you
                  know what service providers we use for this purpose and how some of them allow you
                  to control your ad preferences.
                </Typography>
                <Typography variant="bodyMediumRegular" gutterBottom>
                  We use <BoldStyled>Facebook pixel</BoldStyled> on the Website. Facebook pixel is a
                  code placed on the Website collecting data that helps us track conversions from
                  Facebook ads, build targeted audience and remarket to people who have taken some
                  action on the Website (for example, purchased a meal plan).
                </Typography>
                <Typography variant="bodyMediumRegular" gutterBottom>
                  We use <BoldStyled>Facebook Ads Manager</BoldStyled> together with{' '}
                  <BoldStyled>Facebook Custom Audience</BoldStyled>, which allows us to choose
                  audiences that will see our ads on Facebook or other Facebook’s products (for
                  example, Instagram). Through Facebook Custom Audience we may create a list of
                  users with certain sets of data, such as an IDFA, choose users that have completed
                  certain actions in the Service (for example, visited certain sections of the
                  Website). As a result, we may ask Facebook to show some ads to a particular list
                  of users. As a result, more of our ads may show up while you are using Facebook or
                  other Facebook’s products (for example, Instagram). You may learn how to opt out
                  of advertising provided to you through Facebook Custom Audience{' '}
                  <LinkStyled href="https://www.facebook.com/help/568137493302217">here</LinkStyled>
                  .
                </Typography>
                <Typography variant="bodyMediumRegular" gutterBottom>
                  Facebook also allows its users to influence the types of ads they see on Facebook.
                  To find how to control the ads you see on Facebook, please go{' '}
                  <LinkStyled href="https://www.facebook.com/help/146952742043748?helpref=related">
                    here
                  </LinkStyled>{' '}
                  or adjust your ads settings on{' '}
                  <LinkStyled href="https://www.facebook.com/ads/preferences/?entry_product=ad_settings_screen">
                    Facebook
                  </LinkStyled>
                  .
                </Typography>
                <Typography variant="bodyMediumRegular" gutterBottom>
                  <BoldStyled>Google Ads</BoldStyled> is an ad delivery service provided by Google
                  that can deliver ads to users. In particular, Google allows us to tailor the ads
                  in a way that they will appear, for example, only to users that have conducted
                  certain actions with our Website (for example, show our ads to users who have made
                  a purchase). Some other examples of events that may be used for tailoring ads
                  include, in particular, visiting our Website. Google allows its users to{' '}
                  <LinkStyled href="https://adssettings.google.com/authenticated?hl=ru">
                    opt out of Google’s personalized ads
                  </LinkStyled>{' '}
                  and to
                  <LinkStyled href="https://tools.google.com/dlpage/gaoptout/">
                    prevent their data from being used by Google Analytics
                  </LinkStyled>
                  .
                </Typography>
                <Typography variant="bodyMediumRegular" gutterBottom>
                  <BoldStyled>Google AdSense</BoldStyled> is an ad delivery service provided by
                  Google. Google AdSense enables publishers to monetize content online. AdSense
                  selects ads based on the content of the website and the website&apos;s audience.
                  To know more about how Google processes your data, visit its
                  <LinkStyled href="https://policies.google.com/technologies/partner-sites?hl=en">
                    Privacy Policy
                  </LinkStyled>
                  .
                </Typography>
                <Typography variant="bodyMediumRegular" gutterBottom>
                  We also use <BoldStyled>Snapchat Advertising Platform</BoldStyled> together with{' '}
                  <BoldStyled>Snapchat Audience Based Match</BoldStyled>, which is an ad delivery
                  service provided by Snapchat that can link the activity of some users of our
                  Website with the Snapchat advertising network and show some of our ads to them. As
                  a result, you may see more of ads on Snapchat in case you use our Website.
                  Snapchat allows you to{' '}
                  <LinkStyled href="https://support.snapchat.com/en-US/a/advertising-preferences">
                    Opt Out
                  </LinkStyled>{' '}
                  of their audience based ads.{' '}
                  <LinkStyled href="https://www.snap.com/en-US/privacy/privacy-policy/">
                    Privacy Policy
                  </LinkStyled>
                  .
                </Typography>
                <Typography variant="bodyMediumRegular" gutterBottom>
                  <BoldStyled>The Snap Pixel</BoldStyled> is a piece of JavaScript code that helps
                  Advertisers measure the cross-device impact of Campaigns. Advertisers will be able
                  to see how many Snapchatters take action on their website(s) after seeing their
                  Ad. Snapchat allows you to{' '}
                  <LinkStyled href="https://support.snapchat.com/en-US/a/advertising-preferences">
                    Opt Out
                  </LinkStyled>{' '}
                  of their audience based ads.{' '}
                  <LinkStyled href="https://www.snap.com/en-US/privacy/privacy-policy/">
                    Privacy Policy
                  </LinkStyled>
                  .
                </Typography>
                <Typography variant="bodyMediumRegular" gutterBottom>
                  <BoldStyled>TikTok Ads</BoldStyled> is the service provided by TikTok that can
                  deliver ads to its users. The ads can be tailored to specific categories of users
                  (for instance, based on their geographical location).
                  <LinkStyled href="https://ads.tiktok.com/i18n/official/policy/privacy">
                    TikTok’s Privacy Policy
                  </LinkStyled>
                  .
                </Typography>
                <Typography variant="bodyMediumRegular" gutterBottom>
                  We also use <BoldStyled>Twitter Ads</BoldStyled> provided by Twitter to deliver
                  advertising. Twitter Ads allows us to choose specific audiences based on
                  geographic areas or user’s interests. As a result, we may ask Twitter to deliver
                  our ads to certain list of users. Twitter allows you{' '}
                  <LinkStyled href="https://help.twitter.com/en/safety-and-security/privacy-controls-for-tailored-ads">
                    to opt-out its internet-based advertising
                  </LinkStyled>
                  . <LinkStyled href="https://twitter.com/en/privacy">Privacy Policy</LinkStyled>.
                </Typography>
                <Typography variant="bodyMediumRegular" gutterBottom>
                  We use <BoldStyled>Pinterest Ads</BoldStyled> to deliver group-based
                  advertisements. For example, you may see our ads if you are interested in specific
                  services, information, or offers.{' '}
                  <LinkStyled href="https://policy.pinterest.com/en/privacy-policy">
                    Pinterest Privacy Policy
                  </LinkStyled>
                  .
                </Typography>
              </ListItemStyled>
              <ListItemStyled>
                <BoldStyled>To process your payments</BoldStyled>
                <Typography variant="bodyMediumRegular" gutterBottom>
                  We provide paid products and/or services within the Service. For this purpose, we
                  use third-party services for payment processing (for example, payment processors).
                  As a result of this processing, you will be able to make a payment and we will be
                  notified that the payment has been made. We will not store or collect your payment
                  card details ourselves. This information will be provided directly to our
                  third-party payment processors.
                </Typography>
              </ListItemStyled>
              <ListItemStyled>
                <BoldStyled>
                  To enforce our Terms and Conditions of Use and to prevent and combat fraud
                </BoldStyled>
                <Typography variant="bodyMediumRegular" gutterBottom>
                  We use personal data to enforce our agreements and contractual commitments, to
                  detect, prevent, and combat fraud. As a result of such processing, we may share
                  your information with others, including law enforcement agencies (in particular,
                  if a dispute arises in connection with our Terms and Conditions of Use).
                </Typography>
              </ListItemStyled>
              <ListItemStyled>
                <BoldStyled>To comply with legal obligations</BoldStyled>
                <Typography variant="bodyMediumRegular" gutterBottom>
                  We may process, use, or share your data when the law requires it, in particular,
                  if a law enforcement agency requests your data by available legal means.
                </Typography>
              </ListItemStyled>
            </NumberListStyled>
          </ContainerStyled>
        </ListItemStyled>
        <ListItemStyled id={sections[4].id}>
          <BoldStyled>
            UNDER WHAT LEGAL BASES WE PROCESS YOUR PERSONAL DATA (applies only to EEA-based users)
          </BoldStyled>
          <Typography variant="bodyMediumRegular" gutterBottom>
            In this section, we are letting you know what legal basis we use for each particular
            purpose of processing. For more information on a particular purpose, please refer to{' '}
            <LinkStyled href={sections[3].href}>Section 3</LinkStyled>. This section applies only to
            EEA-based users.
          </Typography>
          <ContainerStyled>
            <Typography variant="bodyMediumRegular" gutterBottom>
              We process your personal data under the following legal bases:
            </Typography>
            <NumberListStyled>
              <ListItemStyled>
                to perform our contract with you;
                <Typography variant="bodyMediumRegular" gutterBottom>
                  Under this legal basis we:
                </Typography>
                <UnorderedListStyled>
                  <ListItemStyled>
                    Provide our Service (in accordance with our Terms and Conditions of Use)
                  </ListItemStyled>
                  <ListItemStyled>Customize your experience</ListItemStyled>
                  <ListItemStyled>Provide you with customer support</ListItemStyled>
                  <ListItemStyled>
                    Communicate with you regarding your use of our Service
                  </ListItemStyled>
                  <ListItemStyled>Process your payments</ListItemStyled>
                </UnorderedListStyled>
              </ListItemStyled>
              <ListItemStyled>
                for our (or others&apos;) legitimate interests, unless those interests are
                overridden by your interests or fundamental rights and freedoms that require
                protection of personal data;
                <Typography variant="bodyMediumRegular" gutterBottom>
                  We rely on legitimate interests:
                </Typography>
                <UnorderedListStyled>
                  <ListItemStyled>
                    to communicate with you regarding your use of our Service
                    <Typography variant="bodyMediumRegular" gutterBottom>
                      This includes, for example, sending you email to notify you that we have
                      released a new product or reminding to finish a purchase. The legitimate
                      interest we rely on for this purpose is our interest to encourage you to use
                      our Service more often.
                    </Typography>
                  </ListItemStyled>
                  <ListItemStyled>
                    to research and analyze your use of the Service
                    <Typography variant="bodyMediumRegular" gutterBottom>
                      Our legitimate interest for this purpose is our interest in improving our
                      Service so that we understand users’ preferences and are able to provide you
                      with a better experience (for example, to make the use of the Website easier
                      and more enjoyable, or to introduce and test new features).
                    </Typography>
                  </ListItemStyled>
                  <ListItemStyled>
                    to send you marketing communications
                    <Typography variant="bodyMediumRegular" gutterBottom>
                      The legitimate interest we rely on for this processing is our interest to
                      promote our Service, including new products and special offers, in a measured
                      and appropriate way.
                    </Typography>
                  </ListItemStyled>
                  <ListItemStyled>
                    to personalize our ads
                    <Typography variant="bodyMediumRegular" gutterBottom>
                      The legitimate interest we rely on for this processing is our interest to
                      promote our Service in a reasonably targeted way.
                    </Typography>
                  </ListItemStyled>
                  <ListItemStyled>
                    to enforce our{' '}
                    <LinkStyled href={BASE_ROUTES.TECH_TERMS}>
                      Terms and Conditions of Use
                    </LinkStyled>{' '}
                    and to prevent and combat fraud
                    <Typography variant="bodyMediumRegular" gutterBottom>
                      Our legitimate interests for this purpose are enforcing our legal rights,
                      preventing and addressing fraud and unauthorised use of the Service,
                      non-compliance with our{' '}
                      <LinkStyled href={BASE_ROUTES.TECH_TERMS}>
                        Terms and Conditions of Use
                      </LinkStyled>
                      .
                    </Typography>
                  </ListItemStyled>
                </UnorderedListStyled>
              </ListItemStyled>
              <ListItemStyled>to comply with legal obligations.</ListItemStyled>
            </NumberListStyled>
          </ContainerStyled>
        </ListItemStyled>
        <ListItemStyled id={sections[5].id}>
          <BoldStyled>WITH WHOM WE SHARE YOUR PERSONAL DATA</BoldStyled>
          <Typography variant="bodyMediumRegular" gutterBottom>
            We share information with third parties that help us operate, provide, improve,
            integrate, customize, support, and market our Service. We may share some sets of
            personal data, in particular, for purposes and with parties indicated in{' '}
            <LinkStyled href={sections[3].href}>Section 3</LinkStyled> of this Privacy Policy. The
            types of third parties we share information with include, in particular:
          </Typography>
          <ContainerStyled>
            <NumberListStyled>
              <ListItemStyled>
                <BoldStyled>Service providers</BoldStyled>
                <Typography variant="bodyMediumRegular" gutterBottom>
                  We share personal data with third parties that we hire to provide services or
                  perform business functions on our behalf, based on our instructions. We may share
                  your personal information with the following types of service providers:
                </Typography>
                <UnorderedListStyled>
                  <ListItemStyled>cloud storage providers (Amazon)</ListItemStyled>
                  <ListItemStyled>
                    data analytics providers (Facebook, Amplitude, Tableau, Google)
                  </ListItemStyled>
                  <ListItemStyled>measurement partners</ListItemStyled>
                  <ListItemStyled>communication service providers (Zendesk)</ListItemStyled>
                  <ListItemStyled>
                    marketing partners (in particular, social media networks, marketing agencies,
                    email delivery services; Facebook, Google, Snapchat, TikTok, Twitter, Pinterest)
                  </ListItemStyled>
                  <ListItemStyled>payment service providers (PayPal, Solid)</ListItemStyled>
                </UnorderedListStyled>
              </ListItemStyled>
              <ListItemStyled>
                <BoldStyled>Law enforcement agencies and other public authorities</BoldStyled>
                <Typography variant="bodyMediumRegular" gutterBottom>
                  We may use and disclose personal data to enforce our{' '}
                  <LinkStyled href={BASE_ROUTES.TECH_TERMS}>Terms and Conditions of Use</LinkStyled>
                  , to protect our rights, privacy, safety, or property, and/or that of our
                  affiliates, you or others, and to respond to requests from courts, law enforcement
                  agencies, regulatory agencies, and other public and government authorities, or in
                  other cases provided for by law.
                </Typography>
              </ListItemStyled>
              <ListItemStyled>
                <BoldStyled>Third parties as part of a merger or acquisition</BoldStyled>
                <Typography variant="bodyMediumRegular" gutterBottom>
                  As we develop our business, we may buy or sell assets or business offerings.
                  Customers’ information is generally one of the transferred business assets in
                  these types of transactions. We may also share such information with any
                  affiliated entity (e.g. parent company or subsidiary) and may transfer such
                  information in the course of a corporate transaction, such as the sale of our
                  business, a divestiture, merger, consolidation, or asset sale, or in the unlikely
                  event of bankruptcy.
                </Typography>
              </ListItemStyled>
            </NumberListStyled>
          </ContainerStyled>
        </ListItemStyled>
        <ListItemStyled id={sections[6].id}>
          <BoldStyled>HOW YOU CAN EXERCISE YOUR PRIVACY RIGHTS</BoldStyled>
          <Typography variant="bodyMediumRegular" gutterBottom>
            To be in control of your personal data, you have the following rights:
          </Typography>
          <Typography variant="bodyMediumRegular" gutterBottom>
            <BoldStyled>
              Accessing / reviewing / updating / correcting your personal data
            </BoldStyled>
            . You may review, edit, or change the personal data that you had previously provided on
            the Website.
          </Typography>
          <Typography variant="bodyMediumRegular" gutterBottom>
            <BoldStyled>Deleting your personal data</BoldStyled>. You can request erasure of your
            personal data as permitted by law.
          </Typography>
          <Typography variant="bodyMediumRegular" gutterBottom>
            When you request deletion of your personal data, we will use reasonable efforts to honor
            your request. In some cases, we may be legally required to keep some of the data for a
            certain time; in such event, we will fulfill your request after we have complied with
            our obligations.
          </Typography>
          <Typography variant="bodyMediumRegular" gutterBottom>
            <BoldStyled>Objecting to or restricting the use of your personal data</BoldStyled>. You
            can ask us to stop using all or some of your personal data or limit our use thereof.
          </Typography>
          <WrapperStyled>
            <Typography variant="bodyMediumRegular" gutterBottom>
              <UnderlineStyled>
                <BoldStyled>Additional information for EEA-based users:</BoldStyled>
              </UnderlineStyled>
            </Typography>
            <Typography variant="bodyMediumRegular" gutterBottom>
              If you are based in the EEA, you have the following rights in addition to the above:
            </Typography>
            <Typography variant="bodyMediumRegular" gutterBottom>
              <BoldStyled>The right to lodge a complaint with supervisory authority</BoldStyled>. We
              would love you to contact us directly, so we could address your concerns.
              Nevertheless, you have the right to lodge a complaint with a competent data protection
              supervisory authority, in particular in the EU Member State where you reside, work or
              where the alleged infringement has taken place.
            </Typography>
            <Typography variant="bodyMediumRegular" gutterBottom>
              <BoldStyled>The right to data portability</BoldStyled>. If you wish to receive your
              personal data in a machine-readable format, you can send respective request to us as
              described below.
            </Typography>
          </WrapperStyled>
          <Typography variant="bodyMediumRegular" gutterBottom>
            To exercise any of your privacy rights, please send a request to{' '}
            <LinkStyled href={config.supportCenter} target="_blank">
              Support center
            </LinkStyled>
            .
          </Typography>
        </ListItemStyled>
        <ListItemStyled id={sections[7].id}>
          <BoldStyled>AGE LIMITATION</BoldStyled>
          <Typography variant="bodyMediumRegular" gutterBottom>
            We do not knowingly process personal data from persons under 16 years of age. If you
            learn that anyone younger than 16 has provided us with personal data, please contact us.
          </Typography>
        </ListItemStyled>
        <ListItemStyled id={sections[8].id}>
          <BoldStyled>INTERNATIONAL DATA TRANSFERS</BoldStyled>
          <Typography variant="bodyMediumRegular" gutterBottom>
            We may transfer personal data to countries other than the country in which the data was
            originally collected in order to provide the Service set forth in the{' '}
            <LinkStyled href={BASE_ROUTES.TECH_TERMS}>Terms and Conditions of Use</LinkStyled> and
            for purposes indicated in this Privacy Policy. If these countries do not have the same
            data protection laws as the country in which you initially provided the information, we
            deploy special safeguards.
          </Typography>
          <Typography variant="bodyMediumRegular" gutterBottom>
            In particular, if we transfer personal data originating from the EEA to countries with
            not adequate level of data protection, we use one of the following legal bases: (i)
            Standard Contractual Clauses approved by the European Commission (details available{' '}
            <LinkStyled href="https://ec.europa.eu/info/law/law-topic/data-protection/data-transfers-outside-eu/model-contracts-transfer-personal-data-third-countries_en">
              here
            </LinkStyled>
            ), or (ii) the European Commission adequacy decisions about certain countries (details
            available{' '}
            <LinkStyled href="https://ec.europa.eu/info/law/law-topic/data-protection/data-transfers-outside-eu/adequacy-protection-personal-data-non-eu-countries_en">
              here
            </LinkStyled>
            ).
          </Typography>
        </ListItemStyled>
        <ListItemStyled id={sections[9].id}>
          <BoldStyled>CHANGES TO THIS PRIVACY POLICY</BoldStyled>
          <Typography variant="bodyMediumRegular" gutterBottom>
            We may modify this Privacy Policy from time to time. If we decide to make material
            changes to this Privacy Policy, you will be notified by available means such as email
            and will have an opportunity to review the revised Privacy Policy. By continuing to
            access or use the Service after those changes become effective, you agree to be bound by
            the revised Privacy Policy.
          </Typography>
        </ListItemStyled>
        <ListItemStyled id={sections[10].id}>
          <BoldStyled>FOR VIRGINIA RESIDENTS</BoldStyled>
          <Typography variant="bodyMediumRegular" gutterBottom>
            This section supplements our Privacy Policy and only applies if you reside in the
            Commonwealth of Virginia. Where applicable, it describes how we use and process your
            personal data and explains your particular rights under Virginia Consumer Data Privacy
            Act (VCDPA).
          </Typography>
          <LetterListStyled>
            <ListItemStyled>
              <BoldStyled>Disclosures about the use of your personal data</BoldStyled>
              <Typography variant="bodyMediumRegular" gutterBottom>
                We may collect and use certain information about you, some of which may be personal
                data (such as your age, gender, date of birth, or other information which may be
                reasonably linked to you), in order to operate the Service and to maximize your
                experience.
              </Typography>
              <Typography variant="bodyMediumRegular" gutterBottom>
                If you would like more information about the categories of your personal data we
                collect or the purposes for which we collect them, please read{' '}
                <LinkStyled href={sections[2].href}>Section 2</LinkStyled> and{' '}
                <LinkStyled href={sections[3].href}>Section 3</LinkStyled>. To learn more about
                sharing of your personal data with our business partners and other third parties,
                please read <LinkStyled href={sections[5].href}>Section 5</LinkStyled>.
              </Typography>
            </ListItemStyled>
            <ListItemStyled>
              <BoldStyled>Data Rights</BoldStyled>
              <Typography variant="bodyMediumRegular" gutterBottom>
                <LinkStyled href={sections[6].href}>Section 6</LinkStyled> of our Privacy Policy
                describes the data rights we offer to all users and how to execute these rights.
                This includes the right to access, review, correct, update your data, obtain a
                portable copy of your data, or delete data related to your stored preferences and
                your use of the Service. Before completing your request, we may require some
                information sufficient to authenticate your identity.
              </Typography>
              <Typography variant="bodyMediumRegular" gutterBottom>
                Additionally, VCDPA provides Virginia residents with these data rights:
              </Typography>
              <UnorderedListStyled>
                <ListItemStyled>
                  <UnderlineStyled>
                    <BoldStyled>
                      Opt out of the Processing of your Personal Data for Targeted Advertising. In
                      order to exercise your choice as a Virginia resident, please contact us at{' '}
                      <LinkStyled href={config.supportCenter} target="_blank">
                        Support center
                      </LinkStyled>
                      .
                    </BoldStyled>
                  </UnderlineStyled>
                  <Typography variant="bodyMediumRegular" gutterBottom>
                    Please note that we do not process personal data for purposes of (1) the sale of
                    personal data, as defined by the VCDPA, or (2) profiling in furtherance of
                    decisions that produce legal or similarly significant effects concerning the
                    consumer.
                  </Typography>
                </ListItemStyled>
                <ListItemStyled>
                  <BoldStyled>Confirm whether your Personal Data is being Processed.</BoldStyled>{' '}
                  You may confirm whether your personal data is being processed by contacting us at{' '}
                  <LinkStyled href={config.supportCenter} target="_blank">
                    Support center
                  </LinkStyled>
                  .
                </ListItemStyled>
                <ListItemStyled>
                  <BoldStyled>Appeal a Case with regard to your request.</BoldStyled> In the case
                  where we declined to take action on your data rights request or have rejected your
                  request, you may contact us at{' '}
                  <LinkStyled href={config.supportCenter} target="_blank">
                    Support center
                  </LinkStyled>{' '}
                  to initiate an appeal of this decision. Please use the subject line “Appeal of
                  Refusal to Take Action on Privacy Request” and provide the relevant information in
                  the email. Once we receive your appeal, we will notify you in writing within 60
                  days of any action taken or not taken in response to the appeal, including a
                  written explanation of the reasons for the decisions.
                </ListItemStyled>
              </UnorderedListStyled>
            </ListItemStyled>
          </LetterListStyled>
          <Typography variant="bodyMediumRegular" gutterBottom>
            If your appeal is denied, you may contact the Office of the Virginia Attorney General
            via the contact details available at{' '}
            <LinkStyled href="http://www.virginia.gov/agencies/office-of-the-attorney-general/#vagov">
              www.virginia.gov/agencies/office-of-the-attorney-general/#vagov
            </LinkStyled>
            .
          </Typography>
        </ListItemStyled>
        <ListItemStyled id={sections[11].id}>
          <BoldStyled>CALIFORNIA PRIVACY RIGHTS</BoldStyled>
          <Typography variant="bodyMediumRegular" gutterBottom>
            California’s Shine the Light law gives California residents the right to ask companies
            once a year what personal information they share with third parties for those third
            parties&apos; direct marketing purposes. Learn more about what is considered to be{' '}
            <LinkStyled href="http://leginfo.legislature.ca.gov/faces/codes_displaySection.xhtml?lawCode=CIV&sectionNum=1798.81.5">
              personal information under the statute
            </LinkStyled>
            .
          </Typography>
          <Typography variant="bodyMediumRegular" gutterBottom>
            To obtain this information from us, please send an email message to{' '}
            <LinkStyled href={config.supportCenter} target="_blank">
              Support center
            </LinkStyled>{' '}
            which includes “Request for California Privacy Information” on the subject line and your
            state of residence and email address in the body of your message. If you are a
            California resident, we will provide the requested information to you at your email
            address in response.
          </Typography>
        </ListItemStyled>
        <ListItemStyled id={sections[12].id}>
          <BoldStyled>DATA RETENTION</BoldStyled>
          <Typography variant="bodyMediumRegular" gutterBottom>
            We will store your personal data for as long as it is reasonably necessary for achieving
            the purposes set forth in this Privacy Policy (including providing the Service to you).
            We will also retain and use your personal data as necessary to comply with our legal
            obligations, resolve disputes, and enforce our agreements.
          </Typography>
        </ListItemStyled>
        <ListItemStyled id={sections[13].id}>
          <BoldStyled>HOW “DO NOT TRACK” REQUESTS ARE HANDLED</BoldStyled>
          <Typography variant="bodyMediumRegular" gutterBottom>
            Except as otherwise stipulated in this Privacy Policy, this Website does not support “Do
            Not Track” requests. To determine whether any of the third-party services it uses honor
            the “Do Not Track” requests, please read their privacy policies.
          </Typography>
        </ListItemStyled>
        <ListItemStyled id={sections[14].id}>
          <BoldStyled>CONTACT US</BoldStyled>
          <Typography variant="bodyMediumRegular" gutterBottom>
            You may contact us at any time for details regarding this Privacy Policy and its
            previous versions. For any questions concerning your account or your personal data
            please contact us at{' '}
            <LinkStyled href={config.supportCenter} target="_blank">
              Support center
            </LinkStyled>
            .
          </Typography>
        </ListItemStyled>
      </NumberListStyled>
      <Typography variant="bodyMediumRegular"> Last updated: {config.dateOfTerms}</Typography>
    </TechLayout>
  );
};
